import { CustomApiException } from "@/exceptions/custom-api-error";
import { QueryKey, UseQueryOptions } from "@tanstack/react-query";
import { AxiosResponse, HttpStatusCode } from "axios";

export enum ApiStatus {
  SUCCESS = "S",
  FAILURE = "F",
}

interface ApiBaseResponse {
  success?: boolean;
  statusCode?: "S" | number | ApiStatus;
  requestId?: string;
  timestamp?: string;
}

export type ApiSuccessResponse<T> = ApiBaseResponse & {
  success?: boolean;
  statusCode: "S" | 200;
  data: T;
};

export type ApiErrorResponse = {
  success?: boolean;
  statusCode?: number;
  requestId?: string;
  timestamp?: string;
  error?: {
    httpCode: HttpStatusCode;
    errorCode?: string;
    description: string;
    error_code?: string
  };
};

export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

export interface ConsentType {
  code: string;
  type: string;
  version: string;
  dateTime: string;
  isAccepted: boolean;
}

export interface ApiLogInfo {
  timeStamp: string;
  url: string; // Request url sub path of the api request made
  requestId: string; // Request ID of the api request
  httpCode: number | undefined; // Http status code of the response
  errorCode: string | undefined; // Error code for any errors in the response object
  message: string | undefined; // Description of an error or a success message
  responseTime: number; // Time in ms that it took for the server to respond
  error_code?: string | undefined;
}

export type ApiQueryOptions<TResponse> = Partial<
  UseQueryOptions<
    AxiosResponse<TResponse>,
    CustomApiException,
    AxiosResponse<TResponse>,
    QueryKey
  >
>;

// query-options.constants.ts
export const defaultQueryOptions = {
  enabled: true,
  staleTime: 5 * 60 * 1000, // 5 minutes
  cacheTime: 10 * 60 * 1000, // 10 minutes
  retry: true,
} as const;

export type QueryOptionsType = Partial<typeof defaultQueryOptions>;
